import { useEffect } from 'react';
import {
  DEFAULT_IMAGE,
  SITE_NAME,
  SITE_URL,
  type OgType,
} from '../seo/publicPageMeta';

const DEFAULT_OG_TYPE: OgType = 'website';

export type { OgType };

interface PageMeta {
  /** Page title (also used for og:title / twitter:title). */
  title: string;
  /** Meta description (also used for og:description / twitter:description). */
  description: string;
  /** Canonical path, e.g. "/" or "/pricing". A full URL is built automatically. */
  path: string;
  /** Optional OG image override. Defaults to the site-wide og-image.png. */
  image?: string;
  /**
   * Optional `og:type` override. Defaults to `'website'`. Set to `'article'`
   * for long-form / documentation pages so social previews and SEO tooling
   * treat the page as an article rather than a generic landing page.
   */
  ogType?: OgType;
  /**
   * Optional ISO-8601 timestamp for when the article was first published.
   * Only emitted when `ogType === 'article'`. Used by `article:published_time`.
   */
  articlePublishedTime?: string;
  /**
   * Optional ISO-8601 timestamp for when the article was last meaningfully
   * updated. Only emitted when `ogType === 'article'`. Used by
   * `article:modified_time`.
   */
  articleModifiedTime?: string;
}

/**
 * Dynamically sets `<title>`, `<meta name="description">`,
 * `<link rel="canonical">`, the full Open Graph set
 * (`og:title` / `og:description` / `og:url` / `og:image` / `og:type` /
 * `og:site_name`), the Twitter card set, and — for `ogType === 'article'`
 * pages — the `article:published_time` / `article:modified_time` tags.
 *
 * Each invocation overwrites the corresponding tags, so subsequent
 * page navigations always show the right metadata. Tags injected by
 * this hook are not removed on unmount — the next page's invocation
 * replaces them, and that's a smaller risk than briefly leaving the
 * document with no `<title>` / canonical between unmount and the next
 * page's effect.
 */
export const usePageMeta = ({
  title,
  description,
  path,
  image,
  ogType = DEFAULT_OG_TYPE,
  articlePublishedTime,
  articleModifiedTime,
}: PageMeta) => {
  useEffect(() => {
    const url = `${SITE_URL}${path}`;
    const ogImage = image ?? DEFAULT_IMAGE;

    // --- title ---
    document.title = title;

    // Helper: set or create a <meta> tag by selector
    const setMeta = (selector: string, content: string) => {
      let el = document.querySelector<HTMLMetaElement>(selector);
      if (!el) {
        el = document.createElement('meta');
        // Derive the attribute from the selector, e.g. 'meta[property="og:title"]'
        const propMatch = selector.match(/\[(\w+)="([^"]+)"\]/);
        if (propMatch) el.setAttribute(propMatch[1], propMatch[2]);
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
    };

    // Helper: remove a <meta> tag if it exists. Used so that navigating
    // from an article page to a website page doesn't leave stale
    // `article:*` timestamps in the DOM that would now describe the
    // wrong page entirely.
    const removeMeta = (selector: string) => {
      const el = document.querySelector<HTMLMetaElement>(selector);
      if (el) el.remove();
    };

    // Helper: set or create <link rel="canonical">
    const setCanonical = (href: string) => {
      let el = document.querySelector<HTMLLinkElement>('link[rel="canonical"]');
      if (!el) {
        el = document.createElement('link');
        el.setAttribute('rel', 'canonical');
        document.head.appendChild(el);
      }
      el.setAttribute('href', href);
    };

    // --- meta description ---
    setMeta('meta[name="description"]', description);

    // --- canonical ---
    setCanonical(url);

    // --- Open Graph ---
    setMeta('meta[property="og:title"]', title);
    setMeta('meta[property="og:description"]', description);
    setMeta('meta[property="og:url"]', url);
    setMeta('meta[property="og:image"]', ogImage);
    setMeta('meta[property="og:site_name"]', SITE_NAME);
    setMeta('meta[property="og:type"]', ogType);

    // --- Article-only OG timestamps ---
    if (ogType === 'article') {
      if (articlePublishedTime) {
        setMeta(
          'meta[property="article:published_time"]',
          articlePublishedTime
        );
      } else {
        removeMeta('meta[property="article:published_time"]');
      }
      if (articleModifiedTime) {
        setMeta('meta[property="article:modified_time"]', articleModifiedTime);
      } else {
        removeMeta('meta[property="article:modified_time"]');
      }
    } else {
      // Navigating from an article to a non-article page — clear the
      // article-only tags so they don't describe the wrong document.
      removeMeta('meta[property="article:published_time"]');
      removeMeta('meta[property="article:modified_time"]');
    }

    // --- Twitter ---
    setMeta('meta[name="twitter:title"]', title);
    setMeta('meta[name="twitter:description"]', description);
    setMeta('meta[name="twitter:url"]', url);
    setMeta('meta[name="twitter:image"]', ogImage);
  }, [
    title,
    description,
    path,
    image,
    ogType,
    articlePublishedTime,
    articleModifiedTime,
  ]);
};
